import { ImageIcon } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";

import { cn } from "@/lib/utils";

import { VS_FONT_FEATURES } from "./constants";
import type { VenueDetails } from "./data";
import { APPLY_HREF, VENUE_BATCH_LDN } from "./data";

interface VsVenueProps {
  className?: string;
  /** Override the section heading. Defaults to "A creative studio unlike
   *  anywhere else in the city."; the Big Pitch sub-page passes its own
   *  copy with a gradient-highlighted phrase. */
  title?: ReactNode;
  /** Override the blue Apply banner copy. Defaults to the Big Pitch sub-page
   *  copy ("Intimate event held exclusively for founders like you."). */
  applyBannerText?: ReactNode;
  /** Override the CTA label and href in the blue banner. */
  applyCta?: { label: string; href: string };
  /** Extra classes merged onto the blue Apply banner — e.g. to shrink its
   *  padding or resize its text. */
  applyBannerClassName?: string;
  /** Extra classes merged onto the Apply banner's text (e.g. to shrink it so
   *  a longer message still fits on one line). */
  applyBannerTextClassName?: string;
  /** Inline style merged onto the blue Apply banner — e.g. to swap the flat
   *  fill for a gradient sourced from a shared JS constant (Tailwind
   *  arbitrary values can't reference those directly). */
  applyBannerStyle?: CSSProperties;
  /** Venue details (logo, photos, map, address). Defaults to Batch LDN; the
   *  summit page passes a New York placeholder. Missing images render neutral
   *  placeholder tiles in the same layout. */
  venue?: VenueDetails;
  /** Extra classes merged onto the heading's wrapping div (default caps it at
   *  806px, which wraps a short one-line title early — widen or remove the
   *  cap for shorter titles). */
  titleWrapperClassName?: string;
  /** Hide the blue Apply CTA banner entirely — e.g. on the retrospective
   *  page, which has no active registration to fund. Defaults to showing it. */
  hideApplyBanner?: boolean;
}

const DEFAULT_TITLE: ReactNode =
  "A creative studio unlike anywhere else in the city.";

/** Render a child wrapped in an external link, or a plain div when no href. */
function MaybeLink({
  href,
  className,
  children,
}: {
  href?: string;
  className?: string;
  children: ReactNode;
}) {
  if (!href) return <div className={className}>{children}</div>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      {children}
    </a>
  );
}

/** Neutral placeholder shown in an image slot when no asset is set. */
function PhotoPlaceholder({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn(
        "flex items-center justify-center border border-[#313335] bg-[#171717]",
        className
      )}
    >
      <ImageIcon size={24} strokeWidth={1.5} className="text-white/20" />
    </div>
  );
}

/**
 * "Venue" — venue photos and a map card with location info, followed by the
 * inline blue Apply CTA banner. Used on the summit page (New York placeholder)
 * and the Big Pitch sub-page (Batch LDN). The heading copy and the venue
 * details differ between the two, so both are overridable props.
 */
export function VsVenue({
  className,
  title = DEFAULT_TITLE,
  applyBannerText = "Intimate event held exclusively for founders like you.",
  applyCta,
  applyBannerClassName,
  applyBannerTextClassName,
  applyBannerStyle,
  venue = VENUE_BATCH_LDN,
  titleWrapperClassName,
  hideApplyBanner = false,
}: VsVenueProps) {
  const ctaHref = applyCta?.href ?? APPLY_HREF;
  const ctaLabel = applyCta?.label ?? "Apply to attend";
  const ctaExternal = !ctaHref.startsWith("#");

  return (
    <div
      id="venue"
      className={cn("scroll-mt-[120px] py-[72px] max-md:py-12", className)}
    >
      <div className={cn("mb-12 max-w-[806px]", titleWrapperClassName)}>
        {venue.logoSrc ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={venue.logoSrc}
            alt={venue.logoAlt ?? venue.name}
            className="mb-5 w-[120px]"
          />
        ) : (
          <p className="mb-5 text-[16px] font-medium tracking-[-0.01em] text-white/80">
            {venue.name}
          </p>
        )}
        <h2
          style={{ fontFeatureSettings: VS_FONT_FEATURES }}
          className="text-[40px] leading-[1.2] font-normal tracking-[-0.04em] text-white max-md:text-[28px]"
        >
          {title}
        </h2>
      </div>

      <div className="flex items-start gap-6 max-md:flex-col">
        <MaybeLink href={venue.websiteHref} className="flex-1 max-md:w-full">
          {venue.primaryImage ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={venue.primaryImage}
              alt={venue.primaryAlt ?? venue.name}
              className="h-[288px] w-full rounded-2xl object-cover max-md:h-[176px]"
            />
          ) : (
            <PhotoPlaceholder className="h-[288px] w-full rounded-2xl max-md:h-[176px]" />
          )}
        </MaybeLink>

        <MaybeLink
          href={venue.mapHref}
          className="relative h-[288px] w-[440px] shrink-0 overflow-hidden rounded-2xl max-md:h-[176px] max-md:w-full"
        >
          {venue.secondaryImage ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={venue.secondaryImage}
              alt={venue.secondaryAlt ?? venue.name}
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : (
            <PhotoPlaceholder className="absolute inset-0 h-full w-full rounded-2xl" />
          )}
          <div
            aria-hidden
            className="absolute inset-0 rounded-2xl bg-black/20"
          />

          {/* Map card */}
          <div className="absolute top-6 right-6 h-[141px] w-[141px] overflow-hidden rounded-lg border-2 border-white/25 bg-white shadow-[9px_12px_15px_0_rgba(0,0,0,0.09)]">
            {venue.mapImage ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={venue.mapImage}
                alt=""
                className="m-1 h-[86px] w-[133px] rounded-sm object-cover"
              />
            ) : (
              <div
                aria-hidden
                className="m-1 flex h-[86px] w-[133px] items-center justify-center rounded-sm bg-[#e9e9e9]"
              >
                <ImageIcon
                  size={20}
                  strokeWidth={1.5}
                  className="text-black/20"
                />
              </div>
            )}
            <div className="px-2 py-1">
              <p className="text-[9px] leading-[1.3] font-medium text-[#0c0d0d]">
                {venue.mapCardTitle ?? venue.name}
              </p>
              {venue.mapCardSubtitle ? (
                <p className="mt-0.5 text-[7px] text-[#62666a]">
                  {venue.mapCardSubtitle}
                </p>
              ) : null}
            </div>
          </div>
        </MaybeLink>
      </div>

      {/* Apply CTA banner — sits directly below the venue photos. Omitted
          entirely when `hideApplyBanner` is set (no active registration to
          drive to, e.g. the retrospective page). */}
      {!hideApplyBanner && (
        <div
          id="apply-venue"
          style={applyBannerStyle}
          className={cn(
            "mt-6 flex flex-wrap items-center justify-between gap-5 rounded-2xl bg-[#001eff] p-12 max-md:flex-col max-md:items-start max-md:p-6",
            applyBannerClassName
          )}
        >
          <p
            style={{ fontFeatureSettings: VS_FONT_FEATURES }}
            className={cn(
              "max-w-[521px] text-[32px] leading-[1.1] font-normal tracking-[-0.04em] text-white max-md:text-[24px]",
              applyBannerTextClassName
            )}
          >
            {applyBannerText}
          </p>
          <a
            href={ctaHref}
            target={ctaExternal ? "_blank" : undefined}
            rel={ctaExternal ? "noopener noreferrer" : undefined}
            style={{ color: "#001eff" }}
            className={cn(
              "inline-flex shrink-0 items-center gap-1 rounded-full whitespace-nowrap",
              "bg-white font-medium",
              "shadow-[0_4px_1px_-2px_rgba(0,0,0,0.06)]",
              "transition-colors hover:bg-white/90 motion-reduce:transition-none",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#001eff]",
              // mobile (≤ md): h-8, 8px padding, 14px text — Figma 111:1437
              "h-8 px-2 text-[14px]",
              // desktop (≥ lg): h-10, 12px padding, 16px text — Figma 116:2361
              "lg:h-10 lg:gap-1.5 lg:px-3 lg:text-[16px]"
            )}
          >
            {ctaLabel}
            <span aria-hidden>→</span>
          </a>
        </div>
      )}
    </div>
  );
}

interface VsVenuesProps {
  className?: string;
  /** Section heading. Defaults to "Venues we've hosted." */
  title?: ReactNode;
  /** Venues to show, one card each, in the order given. */
  venues: readonly VenueDetails[];
  /** Line below the cards linking to the register-interest form. Omitted
   *  entirely when not set. */
  ctaText?: ReactNode;
  ctaHref?: string;
}

/**
 * "Venues we've hosted." — a showcase of every venue the Summit has run at,
 * shown side by side as simple photo + link cards. Unlike `VsVenue` (a
 * single detailed venue plus an Apply banner, still used by the Big Pitch
 * sub-page), this has no CTA per card — just a name, city, and a link to
 * the venue's own site — with one shared line below linking to the
 * register-interest form instead. Used on the retrospective summit page in
 * place of the old single-venue promo.
 */
export function VsVenues({
  className,
  title = "Venues we've hosted.",
  venues,
  ctaText,
  ctaHref = "#register",
}: VsVenuesProps) {
  return (
    <div
      id="venue"
      className={cn("scroll-mt-[120px] py-[72px] max-md:py-12", className)}
    >
      <h2
        style={{ fontFeatureSettings: VS_FONT_FEATURES }}
        className="mb-10 text-[40px] leading-[1.2] font-normal tracking-[-0.04em] text-white max-md:mb-8 max-md:text-[28px]"
      >
        {title}
      </h2>

      <div className="grid grid-cols-2 gap-6 max-md:grid-cols-1">
        {venues.map((venue) => (
          <VenueCard key={venue.name} venue={venue} />
        ))}
      </div>

      {ctaText ? (
        <a
          href={ctaHref}
          className={cn(
            "mt-8 inline-flex items-center gap-1 text-[16px] text-white/70",
            "transition-colors hover:text-white motion-reduce:transition-none",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0d0d]"
          )}
        >
          {ctaText}
          <span aria-hidden>→</span>
        </a>
      ) : null}
    </div>
  );
}

/** One venue card — photo on the left, name/city and a "Visit site" link on
 *  the right; stacks (photo on top) on mobile. */
function VenueCard({ venue }: { venue: VenueDetails }) {
  return (
    <div className="flex items-center gap-5 rounded-2xl bg-[#171717] p-5 max-md:flex-col max-md:items-start">
      <MaybeLink
        href={venue.websiteHref}
        className="h-[160px] w-[200px] shrink-0 overflow-hidden rounded-xl max-md:h-[176px] max-md:w-full"
      >
        {venue.primaryImage ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={venue.primaryImage}
            alt={venue.primaryAlt ?? venue.name}
            className="h-full w-full object-cover"
          />
        ) : (
          <PhotoPlaceholder className="h-full w-full rounded-xl" />
        )}
      </MaybeLink>

      <div>
        <h3 className="text-[20px] font-normal tracking-[-0.01em] text-white">
          {venue.name}
        </h3>
        {venue.city ? (
          <p className="mt-1 text-[14px] text-white/55">{venue.city}</p>
        ) : null}
        {venue.websiteHref ? (
          <a
            href={venue.websiteHref}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "mt-3 inline-flex items-center gap-1 text-[14px] text-[#95999d]",
              "transition-colors hover:text-[#f9f9f9] motion-reduce:transition-none",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-1 focus-visible:ring-offset-[#171717]"
            )}
          >
            Visit site <span aria-hidden>→</span>
          </a>
        ) : null}
      </div>
    </div>
  );
}
